import React, { memo } from 'react';
import { mergeClasses, ProgressBar } from '@fluentui/react-components';
import { Star16Filled } from '@fluentui/react-icons';
import { formatRequiredPoints } from 'src/utils/utils';
import { useStyles } from './RewardProgressBar.styles';

interface RewardProgressBarProps {
  totalPointsEarned: number
  rewardsRequiredPoints: number[]
  customClassName?: string
}

const getEarnedPointsIndex = (cumulativePoints: number[], totalPointsEarned: number): number => {
  return cumulativePoints.findIndex((point, index) => {
    const isLastPoint = index === cumulativePoints.length - 1;

    if (isLastPoint) {
      return totalPointsEarned >= point;
    }
    else {
      const isWithinRange = totalPointsEarned < cumulativePoints[index + 1];
      return totalPointsEarned >= point && isWithinRange;
    }
  });
};

const calculateProgress = (
  index: number,
  cumulativePoints: number[],
  earnedPointsIndex: number,
  totalPointsEarned: number,
): number => {
  if (index < earnedPointsIndex) return 100;
  if (index > earnedPointsIndex || totalPointsEarned < 0) return 0;

  const [currentPoint, nextPoint] = [cumulativePoints[index], cumulativePoints[index + 1]];
  return Math.min(100, ((totalPointsEarned - currentPoint) / (nextPoint - currentPoint)) * 100);
};

const getStarMilestoneIndex = (earnedPointsIndex: number, totalMilestonesCount: number): number => {
  const isFinalMilestoneReached = earnedPointsIndex === totalMilestonesCount - 1;
  return isFinalMilestoneReached ? -1 : earnedPointsIndex + 1;
};

export const RewardProgressBar: React.FC<RewardProgressBarProps> = ({
  totalPointsEarned,
  rewardsRequiredPoints,
  customClassName,
}) => {
  const styles = useStyles();
  const cumulativePoints = rewardsRequiredPoints.length > 0 ? [0, ...rewardsRequiredPoints] : [];
  const earnedPointsIndex = getEarnedPointsIndex(cumulativePoints, totalPointsEarned);
  const starMilestoneIndex = getStarMilestoneIndex(earnedPointsIndex, cumulativePoints.length);

  const renderMilestones = (): JSX.Element[] => {
    return cumulativePoints.map((point, index) => {
      const milestonePosition = (index / (cumulativePoints.length - 1)) * 100;
      const showStar = index === starMilestoneIndex && starMilestoneIndex !== -1;
      const isFirst = index === 0;
      const isMilestoneReached = totalPointsEarned !== 0 && totalPointsEarned >= point;

      return (
        <div
          key={point}
          className={mergeClasses('milestone-item', styles.milestoneItem)}
          style={{ left: `${milestonePosition}%` }}
        >
          {showStar && (
            <div className={styles.starIconContainer}>
              <Star16Filled className={styles.starIcon} data-testid="star-icon" />
            </div>
          )}
          <div
            className={mergeClasses(
              styles.milestoneDot,
              isFirst && styles.firstMilestoneDot,
              isMilestoneReached && styles.milestoneDotReached,
            )}
          />

          <span className={mergeClasses(styles.milestonePoint, isMilestoneReached && styles.milestonePointReached)}>
            {formatRequiredPoints(point)}
          </span>
        </div>
      );
    });
  };

  const renderProgressBars = (): JSX.Element[] => {
    return cumulativePoints.slice(0, -1).map((_, index) => {
      const progress = calculateProgress(index, cumulativePoints, earnedPointsIndex, totalPointsEarned);

      return (
        <div key={cumulativePoints[index]} className={styles.progressSegment}>
          <ProgressBar
            value={progress}
            max={100}
            shape="square"
            thickness="large"
            className={mergeClasses('progress-bar', styles.progressBar, customClassName)}
          />
        </div>
      );
    });
  };

  return (
    <div className={styles.container}>
      <div className={mergeClasses('reward-milestone-row', styles.milestonesRow)}>{renderMilestones()}</div>

      <div className={mergeClasses('reward-progress-bar-row', styles.progressBarsRow)}>{renderProgressBars()}</div>
    </div>
  );
};

export default memo(RewardProgressBar);
